/**
 * Admin -- Audit/Change Sets (Wave 1b W1b-31)
 *
 * Card-style list of "change sets" -- contiguous activity by the same
 * actor in a 15-minute window. Click-to-detail sheet with timeline view.
 *
 * Backend: /api/v1/admin/audit/changes (W1b-31).
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  GitCommit,
  User as UserIcon,
  AlertTriangle,
  Clock,
  Loader2,
  Hash,
  Shield,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { relativeTime } from '@/lib/format';
import { apiClient } from '@/lib/api-client';

export const Route = createFileRoute('/admin/audit/changes')({
  component: ChangesPage,
});

type RiskScore = 'medium' | 'high' | 'critical';

interface ChangeSetSummary {
  id: string;
  actorId: string | null;
  actorEmail: string | null;
  actorType: string;
  windowStart: string;
  windowEnd: string;
  eventCount: number;
  riskScore: RiskScore;
  eventTypes: string[];
}

interface ChangeSetEvent {
  id: string;
  sequenceNumber: string;
  eventType: string;
  resourceType: string;
  resourceId: string | null;
  createdAt: string;
  recordHash: string;
  eventData: unknown;
}

interface ChangeSetDetail extends ChangeSetSummary {
  events: ChangeSetEvent[];
}

function ChangesPage() {
  const [searchText, setSearchText] = useState('');
  const [riskScore, setRiskScore] = useState<'all' | RiskScore>('all');
  const [sinceDays, setSinceDays] = useState<string>('all');
  const [viewingId, setViewingId] = useState<string | null>(null);

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (searchText.trim()) p.set('searchText', searchText.trim());
    if (riskScore !== 'all') p.set('riskScore', riskScore);
    if (sinceDays !== 'all') {
      const d = new Date();
      d.setDate(d.getDate() - Number(sinceDays));
      p.set('since', d.toISOString());
    }
    p.set('limit', '50');
    return p;
  }, [searchText, riskScore, sinceDays]);

  const listQuery = useQuery({
    queryKey: ['audit', 'changes', queryParams.toString()],
    queryFn: () =>
      apiClient.get<{ items: ChangeSetSummary[]; total: number }>(
        `/admin/audit/changes?${queryParams.toString()}`
      ),
  });

  const detailQuery = useQuery({
    queryKey: ['audit', 'changes', 'detail', viewingId],
    queryFn: () =>
      viewingId
        ? apiClient.get<ChangeSetDetail>(
            `/admin/audit/changes/${encodeURIComponent(viewingId)}`
          )
        : Promise.reject(new Error('no id')),
    enabled: !!viewingId,
  });

  const items = listQuery.data?.items ?? [];

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <GitCommit className="h-5 w-5" />
          Change Sets
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Adjacent same-actor events grouped into 15-minute windows. Useful
          for forensic narrative ("admin X did the following 7 things in
          their 2:35 PM session").
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search event payload…"
          className="w-64 h-8 text-xs"
        />
        <Select
          value={riskScore}
          onValueChange={(v) => setRiskScore(v as 'all' | RiskScore)}
        >
          <SelectTrigger className="w-36 h-8 text-xs">
            <SelectValue placeholder="Risk score" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All risk</SelectItem>
            <SelectItem value="critical">Critical</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sinceDays} onValueChange={setSinceDays}>
          <SelectTrigger className="w-32 h-8 text-xs">
            <SelectValue placeholder="Time range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="1">Last 24h</SelectItem>
            <SelectItem value="7">Last 7d</SelectItem>
            <SelectItem value="30">Last 30d</SelectItem>
            <SelectItem value="90">Last 90d</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="text-xs text-muted-foreground">
        {listQuery.isLoading
          ? 'Loading…'
          : `${items.length} of ${listQuery.data?.total ?? 0} change sets`}
      </div>

      {/* Card list */}
      <div className="space-y-3">
        {items.map((cs) => (
          <Card
            key={cs.id}
            className="cursor-pointer hover:bg-muted/30 transition"
            onClick={() => setViewingId(cs.id)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <UserIcon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-medium text-sm">
                      {cs.actorEmail ?? (
                        <span className="italic text-muted-foreground">
                          {cs.actorType}
                        </span>
                      )}
                    </span>
                    <RiskBadge score={cs.riskScore} />
                    <Badge variant="outline" className="h-5 text-[10px] gap-1">
                      <Clock className="h-3 w-3" />
                      {cs.eventCount} event{cs.eventCount === 1 ? '' : 's'}
                    </Badge>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    {relativeTime(cs.windowEnd)} • window{' '}
                    {new Date(cs.windowStart).toLocaleTimeString()} →{' '}
                    {new Date(cs.windowEnd).toLocaleTimeString()}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {cs.eventTypes.slice(0, 5).map((t) => (
                      <Badge
                        key={t}
                        variant="outline"
                        className="font-mono text-[10px] h-5"
                      >
                        {t}
                      </Badge>
                    ))}
                    {cs.eventTypes.length > 5 && (
                      <Badge variant="outline" className="text-[10px] h-5">
                        +{cs.eventTypes.length - 5} more
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && !listQuery.isLoading && (
          <div className="text-center py-10 text-sm text-muted-foreground">
            No change sets match your filters.
          </div>
        )}
      </div>

      {/* Detail sheet */}
      <Sheet open={!!viewingId} onOpenChange={(o) => !o && setViewingId(null)}>
        <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Change set</SheetTitle>
            <SheetDescription>
              {detailQuery.data?.actorEmail ?? detailQuery.data?.actorType}
              {' • '}
              {detailQuery.data?.eventCount} events
            </SheetDescription>
          </SheetHeader>

          {detailQuery.isLoading && (
            <div className="flex items-center gap-2 mt-4 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading events…
            </div>
          )}

          {detailQuery.data && (
            <div className="mt-4 space-y-4 text-sm">
              <div className="flex flex-wrap gap-2">
                <RiskBadge score={detailQuery.data.riskScore} />
                <Badge variant="outline" className="text-[10px]">
                  Window:{' '}
                  {new Date(detailQuery.data.windowStart).toLocaleString()}
                </Badge>
              </div>
              <Separator />
              <div className="text-xs text-muted-foreground">
                Events (chronological):
              </div>
              <ol className="space-y-2 relative">
                {detailQuery.data.events.map((ev) => (
                  <li
                    key={ev.id}
                    className={cn(
                      'border rounded p-2 text-xs space-y-1',
                      'hover:bg-muted/30'
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <code className="font-mono">{ev.eventType}</code>
                      <span className="text-muted-foreground text-[10px]">
                        seq {ev.sequenceNumber} •{' '}
                        {new Date(ev.createdAt).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="text-[10px] text-muted-foreground">
                      {ev.resourceType}
                      {ev.resourceId && (
                        <span className="font-mono ml-1">
                          {ev.resourceId.slice(0, 8)}…
                        </span>
                      )}
                    </div>
                    <div className="flex items-start gap-1 font-mono text-[10px] text-muted-foreground">
                      <Hash className="h-2.5 w-2.5 mt-0.5" />
                      <span className="break-all">
                        {ev.recordHash.slice(0, 32)}…
                      </span>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function RiskBadge({ score }: { score: RiskScore }) {
  if (score === 'critical') {
    return (
      <Badge
        variant="outline"
        className="border-red-500/40 bg-red-500/10 text-red-600 gap-1 h-5 text-[10px]"
      >
        <AlertTriangle className="h-3 w-3" />
        Critical risk
      </Badge>
    );
  }
  if (score === 'high') {
    return (
      <Badge
        variant="outline"
        className="border-amber-500/40 bg-amber-500/10 text-amber-600 gap-1 h-5 text-[10px]"
      >
        <Shield className="h-3 w-3" />
        High risk
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="border-slate-500/40 bg-slate-500/10 text-slate-600 h-5 text-[10px]"
    >
      Medium
    </Badge>
  );
}
